import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LoginForm } from '@/components/auth/LoginForm';
import { RegisterForm } from '@/components/auth/RegisterForm';
import { fetchConfig } from '@/api/trip';
import { tripKeys } from '@/api/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import authStyles from '@/components/auth/AuthForms.module.css';
import styles from './AuthPage.module.css';

interface AuthPageProps {
  slug: string;
}

type Tab = 'login' | 'register';

export default function AuthPage({ slug }: AuthPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || undefined;
  const initialTab: Tab = inviteToken ? 'register' : 'login';
  const [tab, setTab] = useState<Tab>(initialTab);
  const { isAuthenticated } = useAuth(slug);

  const configQuery = useQuery({
    queryKey: tripKeys.config(slug),
    queryFn: ({ signal }) => fetchConfig(slug, signal),
  });

  // Bereits eingeloggt? Sofort auf die Hauptseite umleiten — kein Sinn die
  // Auth-Form zu zeigen. (Login-Erfolg ruft separat `onSuccess` mit navigate auf.)
  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  function handleSuccess() {
    navigate('/', { replace: true });
  }

  const tripHeader = configQuery.data?.trip.header ?? 'Trip';
  const tripDate = configQuery.data?.trip.dateBadge;

  return (
    <div className="app-shell">
      <div className={styles.page}>
        <div className={styles.hero} aria-hidden="true">
          <div className={styles.sun} />
          <div className={styles.waves}>
            <span />
            <span />
            <span />
          </div>
        </div>

        <div className={styles.wrap}>
          <div className={styles.brand}>
            <h1 className={styles.wordmark}>MALLELUJA</h1>
            <div className={styles.tagline}>
              {tripHeader}
              {tripDate ? ` · ${tripDate}` : null}
            </div>
          </div>

          <div className={styles.card}>
            <p className={styles.subtitle}>
              Melde dich an oder erstelle einen Account, um der Crew beizutreten.
            </p>
            <div className={authStyles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'login'}
                className={`${authStyles.tab} ${tab === 'login' ? authStyles.tabActive : ''}`}
                onClick={() => setTab('login')}
              >
                Einloggen
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'register'}
                className={`${authStyles.tab} ${tab === 'register' ? authStyles.tabActive : ''}`}
                onClick={() => setTab('register')}
              >
                Registrieren
              </button>
            </div>
            {tab === 'login'
              ? <LoginForm slug={slug} onSuccess={handleSuccess} />
              : <RegisterForm slug={slug} inviteToken={inviteToken} onSuccess={handleSuccess} />}
          </div>

          <div className={styles.foot}>
            Trip-Code vergessen? Frag den Admin — er hat alle Invites.
          </div>
        </div>
      </div>
    </div>
  );
}
